import { db } from "./offline-db";

/**
 * Cached session shape stored in IndexedDB.
 * Contains just enough to render the app shell and route guards offline.
 */
export interface CachedSession {
  userId: string;
  name: string;
  email: string;
  role: string | null;
  orgId: string | null;
  image: string | null;
  /** The session token (for display/reference only -- NOT used for auth when back online) */
  token: string;
  /** ISO timestamp of when this was cached */
  cachedAt: string;
}

const STORE_KEY = "offline-session";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Cache the user's session and profile to IndexedDB after a successful
 * online login or session validation.
 */
export async function cacheSession(
  user: {
    id: string;
    name: string;
    email: string;
    role?: string | null;
    image?: string | null;
  },
  session: {
    token?: string;
    id?: string;
  },
  orgId?: string | null
): Promise<void> {
  const entry: CachedSession = {
    userId: user.id,
    name: user.name,
    email: user.email,
    role: (user.role as string) ?? null,
    orgId: orgId ?? null,
    image: user.image ?? null,
    token: session.token || session.id || "",
    cachedAt: new Date().toISOString(),
  };
  await db.catalogs.put({
    key: STORE_KEY,
    data: entry,
    cachedAt: entry.cachedAt,
    expiresAt: new Date(Date.now() + MAX_AGE_MS).toISOString(),
  });
}

/**
 * Retrieve the cached session from IndexedDB.
 * Returns null if no cached session exists or if it is older than 24 hours.
 */
export async function getCachedSession(): Promise<CachedSession | null> {
  try {
    const entry = await db.catalogs.get(STORE_KEY);
    if (!entry || !entry.data) return null;

    const cached = entry.data as CachedSession;
    const age = Date.now() - new Date(cached.cachedAt).getTime();
    if (age > MAX_AGE_MS) {
      // Stale -- remove it
      await db.catalogs.delete(STORE_KEY);
      return null;
    }

    return cached;
  } catch {
    return null;
  }
}

/**
 * Clear the cached session (on logout).
 */
export async function clearCachedSession(): Promise<void> {
  try {
    await db.catalogs.delete(STORE_KEY);
  } catch {
    // Silently ignore -- IndexedDB might not be available
  }
}

/**
 * Build a mock "user" and "session" object compatible with the shapes
 * expected by TanStack Router route context from Better Auth's getSession().
 *
 * IMPORTANT: All date fields use ISO strings (not Date objects) to prevent
 * React Error #310 ("Objects are not valid as a React child") if any
 * component accidentally renders a date field from the user/session context.
 */
export function buildOfflineContext(cached: CachedSession) {
  const now = new Date().toISOString();
  const expiresAt = new Date(Date.now() + MAX_AGE_MS).toISOString();

  return {
    user: {
      id: cached.userId,
      name: cached.name,
      email: cached.email,
      role: cached.role,
      image: cached.image,
      emailVerified: true,
      createdAt: now,
      updatedAt: now,
    },
    session: {
      id: cached.token,
      token: cached.token,
      userId: cached.userId,
      expiresAt,
      createdAt: now,
      updatedAt: now,
      ipAddress: null,
      userAgent: null,
    },
  };
}
